import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ClinicDTO } from '@core/apis/clinic/clinic.dto';
import { MeasurementApi } from '@core/apis/measurement/measurement.api';
import { MeasurementDeviceDTO, MeasurementDTO } from '@core/apis/measurement/measurement.dto';
import { IMeasurementEcg, IMeasurementOs } from '@core/apis/measurement/measurement.interface';
import { StaffDTO } from '@core/apis/staff/staff.dto';
import { TranslocoHelper } from '@core/helpers/transloco.helper';
import { IPaginationGet } from '@core/interfaces/interface';
import { ClinicStore } from '@core/stores/clinic.store';
import { StaffStore } from '@core/stores/staff.store';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetAllMeasurementsInput, MeasurementRecordPostureInput, MeasurementTypeEnum } from '@oranta-mis/sdk';
import {
  ColumnPersonEnum,
  IButtonClickEvent,
  ITableChangesConfig,
  ITableRow,
  Table,
  TableColumn,
} from '@oranta-mis/ui-library';
import { ConfirmDownloadMeasurementDialogComponent } from './confirm-download-measurement-dialog/confirm-download-measurement-dialog.component';
import { IdentifyMeasurementDialogComponent } from './identify-measurement-dialog/identify-measurement-dialog.component';
import { measurementListColumnHeader } from './measurement';

interface IRenderTable extends ITableRow<MeasurementDTO> {
  type: string;
  info: string;
  registrationDate: string | null;
  createdAt: string | null;
}

@Component({
  selector: 'app-measurement-list',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    ConfirmDownloadMeasurementDialogComponent,
    IdentifyMeasurementDialogComponent,
    Table,
  ],
  templateUrl: './measurement-list.component.html',
  styleUrl: './measurement-list.component.scss',
})
@UntilDestroy()
export class MeasurementListComponent {
  public columnHeader!: TableColumn[];
  public measurementasHistoryPaginated!: IPaginationGet<MeasurementDTO[]>;
  public measurementasHistory: IRenderTable[] = [];
  public isLoading: boolean = true;

  public selectedItems: MeasurementDTO[] = [];
  public staff: StaffDTO | null = null;
  public clinic: ClinicDTO | null = null;

  public config!: ITableChangesConfig;

  @ViewChild(ConfirmDownloadMeasurementDialogComponent)
  confirmDownloadMeasurementDialog!: ConfirmDownloadMeasurementDialogComponent;

  @ViewChild(IdentifyMeasurementDialogComponent)
  identifyMeasurementDialog!: IdentifyMeasurementDialogComponent;

  @ViewChild(Table)
  tableComponent!: Table;

  constructor(
    private readonly measurementApi: MeasurementApi,
    private readonly staffStore: StaffStore,
    private readonly clinicStore: ClinicStore,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly i18n: TranslocoHelper,
  ) {}

  ngOnInit(): void {
    this.columnHeader = measurementListColumnHeader(this.i18n);
    this.staff = this.staffStore.get();
    this.clinic = this.clinicStore.get();
  }

  public onPersonRouteTo(row: IRenderTable): void {
    this.router.navigate([`../${row.person?.data?.id || 0}/${row.item['type'].replaceAll('_', '-')}`], {
      relativeTo: this.route,
    });
  }

  public resetSelectedRows(): void {
    this.selectedItems = [];
    this.tableComponent.resetCheckboxes();
  }

  public allChecked(): boolean {
    if (!this.measurementasHistoryPaginated?.list?.length) return false;
    return this.measurementasHistoryPaginated.list.every((item) =>
      this.selectedItems.map((s) => s.id).includes(item.id),
    );
  }

  public download(): void {
    this.confirmDownloadMeasurementDialog.selectedItems = this.selectedItems;
    this.confirmDownloadMeasurementDialog.openModal();
  }

  public setConfig(config: ITableChangesConfig): void {
    this.config = config;
    this.getMeasurements(this.config);
  }

  public getMeasurements(config: ITableChangesConfig): void {
    this.isLoading = true;
    let settings: GetAllMeasurementsInput = {
      pagination: {
        page: config?.page || 1,
        perPage: config?.perPage || 10,
      },
      where: { clinicId: this.clinic?.id, staffId: this.staff?.id },
    };

    if (config?.orderBy) settings.orderBy = config.orderBy;
    if (config?.filters) settings.where = { ...(settings.where || {}), ...config.filters };

    this.measurementApi
      .get(settings)
      .pipe(untilDestroyed(this))
      .subscribe((measurement) => {
        this.measurementasHistoryPaginated = measurement;
        this.measurementasHistory = this.renderObject(measurement.list);
        this.isLoading = false;
      });
  }

  private generateMeasurementInfo(measurement: MeasurementDTO): string {
    let info: string = '-';
    if (measurement['type'] === MeasurementTypeEnum.ecg) {
      info = `${this.i18n.tr('chss')}:&nbsp;${(measurement.records as IMeasurementEcg).leads[0].results.summary.hrStatistics.averageHr}`;
    } else if (measurement['type'] === MeasurementTypeEnum.os) {
      const timeAnalysis = (measurement.records as IMeasurementOs).results.timeAnalysis;
      info = `${this.i18n.tr('ats')}:&nbsp;${timeAnalysis?.systola}, ${this.i18n.tr('atd')}:&nbsp;${
        timeAnalysis?.diastola
      }, ${this.i18n.tr('chss')}:&nbsp;${timeAnalysis?.heartRate}`;
    } else if (measurement['type'] === MeasurementTypeEnum.posture) {
      const records = measurement.records as MeasurementRecordPostureInput;
      info = `${this.i18n.tr('measurement.' + records.exerciseType)}&nbsp;(${this.i18n.tr('measurement.' + records.dataSource)})`;
    }
    return info;
  }

  private renderObject(arr: MeasurementDTO[]): IRenderTable[] {
    const datePipe = new DatePipe('en-US');
    return arr.map((v) => {
      let type = `${this.i18n.tr(v['type'])}`;
      const device = v['device'] as null | MeasurementDeviceDTO[];
      if (device && device.length > 0) type += ` (${device.map((d) => `${d.name}`).join(', ')})`;

      return {
        item: v,
        id: v.id,
        info: this.generateMeasurementInfo(v),
        type: type,
        registrationDate: datePipe.transform(v.registrationDate, 'dd/MM/YYYY, HH:mm'),
        createdAt: datePipe.transform(v.createdAt, 'dd/MM/YYYY, HH:mm'),
        person: {
          type: ColumnPersonEnum.patient,
          data: v.patient,
        },
        buttonConfig: {
          identify: {
            action: 'identify',
            icon: 'bi-person-badge',
            label: 'identify',
            disabled: false,
            hidden: !(!v.patient?.id && (v['type'] == MeasurementTypeEnum.ecg || v['type'] == MeasurementTypeEnum.os)),
          },
        },
      };
    });
  }

  public deleteSelected(): void {
    this.isLoading = true;
    this.measurementApi
      .remove(this.selectedItems.map((v) => v.id))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.config.page = 1;
          this.getMeasurements(this.config);
          this.selectedItems = [];
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  public onCheckboxChange(selectedRows: IRenderTable[]) {
    this.selectedItems = selectedRows.map((row) => row.item);
  }

  public onTableButtonClick(event: IButtonClickEvent<IRenderTable>) {
    switch (event.action) {
      case 'identify':
        this.openIdentifyModal(event.row.item);
        break;
      default:
        console.warn(`No handler for action: ${event.action}`);
        break;
    }
  }

  private openIdentifyModal(item: MeasurementDTO): void {
    this.identifyMeasurementDialog.measurementId = item.id;
    this.identifyMeasurementDialog.openModal();
  }
}
